import { GenericResponse } from './../../../shared/interfaces/generic-response.interface';
// Angular
import { Injectable } from '@angular/core';

// NGRX && RXJS
import { Observable, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { map, filter, take, switchMap, catchError } from 'rxjs/operators';

import moment from 'moment';
import * as _ from 'lodash';
import * as utils from '../../../util';
import * as fromRoot from '../../../reducers';
import * as olci from '../../../actions/olci';
import { BookingService } from './booking.service';
import { State } from '../../../reducers/guest-details';
import * as guestActions from '../../../actions/guest.actions';
import { ReservationRetrievalResponse } from '../../../models';
import { MicroServicesService } from './micro-services.service';
import { Guest, ModuleSpecificValue } from '../../../models/guest';
import { constants, analyticsConstants } from '../../../constants';
import {
  IRetrieveCheckinStatusRequestPayload,
  IRetrieveCheckinStatusResponse
} from '../../../models/action-payloads/retrieve-checkin-status-payload';
import * as guestDetailsActions from '../../../actions/guest-details.actions';
import { ITravelQuestionnaire } from '../../../models/travel-questionnaire.payload';
import {
  ProfileBooking,
  IProfileBookingPayload,
  Passenger,
  ProfileBookingPayload,
} from '../../../models/action-payloads/profile-booking-payload';
import * as actions from '../../../actions';
import { formatDateWithoutDashes } from '../../../utils/date.utils';

@Injectable()
export class GuestService {
	public _guests: Guest[] | any = [];
	public _guestsCompleted: Guest[] = [];

	public isSmartShip: boolean;
	public isArrivalTimesEnabled: boolean;
	public firstPageModules: string[];
	public _state: State;
	public sessionSnapshot: ReservationRetrievalResponse;
	public travelQuestionnaireSection: any;
	public guestProfileBookingData: IProfileBookingPayload;

  constructor(
    private readonly store: Store<fromRoot.AppState>,
    private microService: MicroServicesService,
    private bookingService: BookingService
  ) {
    store
      .pipe(select(fromRoot.getEntitySGuests))
      .subscribe((guests) => (this._guests = guests));
    store
      .pipe(select(fromRoot.getGuestListCompleted))
      .subscribe(
        (guestsCompleted) => (this._guestsCompleted = guestsCompleted)
      );
    store
      .pipe(
        select(fromRoot.getSessionVar),
        filter((vars) => vars !== null),
        take(1)
      )
      .subscribe((sessionVars) => {
        const { enhancedSSP, arrivalTimeEnabled } = sessionVars;
        this.isSmartShip = enhancedSSP;
        this.isArrivalTimesEnabled = arrivalTimeEnabled;
        this.sessionSnapshot = { ...sessionVars };
      });
    this.setGuestDetailsModules();
  }

  public setGuestDetailsModules(): void {
    this.firstPageModules = [
			constants.MODULES_NAMES.PERSONAL_DETAIL,
			constants.MODULES_NAMES.ADDRESS_CONTACT,
			constants.MODULES_NAMES.IMMIGRATION_INFO,
			constants.MODULES_NAMES.CONTACT_INFO,
			constants.MODULES_NAMES.EMERGENCY_CONTACT
		]
  }

	get isInTheStore(): boolean {
		if (!this._guests || this._guests.length === 0) {
			return false;
		}

		return this._guests.length > 0;
	}

	get allGuests(): Guest[] {
		return this._guests;
	}

	private isFeatureCompleted(modules: string[]): Observable<boolean> {
		return this.store.pipe(
			select(fromRoot.progressIsLoaded),
			filter(p => p === true),
			map(r => {
				let result = false;
				this.store
					.pipe(select(fromRoot.getSelectdGuestsForCheckinProcess))
					.pipe(take(1))
					.subscribe(guests => {
						if (guests.length === 0) {
							return false;
						}
						const noComplete = guests.filter((g: any) => {
							let anyIncomplete = false;
							modules.forEach(module => {
								if (!_.get(g, `moduleSpecificValue.${module}`, false)) {
									anyIncomplete = true;
								}
							});
							return anyIncomplete;
						});
						result = noComplete.length === 0;
					});

				return result;
			}),
			take(1)
		);
	}

	updatePercentageCompletionAllGuests(guests: IRetrieveCheckinStatusRequestPayload) {
		this.store.dispatch(new guestActions.GetPercentageCompletionAction(guests));

		return this.store.pipe(select(fromRoot.progressIsLoaded)).pipe(
			filter(progressIsLoaded => progressIsLoaded),
			take(1),
			switchMap(() => {
				return this.store.pipe(
					select(fromRoot.guestStateError),
					take(1),
					map(error => (error ? 2 : 0))
				);
			})
		);
	}

	getCruiseSurvey() {
		let result;
		this.store
			.pipe(
				select(fromRoot.guestDetailsState),
				take(1)
			)
			.subscribe(state => (this._state = state));

		if (this.loggedInGuestPaxId && this._state.guestDetails[this.loggedInGuestPaxId] !== undefined) {
			const loggedInGuest = this._state.guestDetails[this.loggedInGuestPaxId];
			result = loggedInGuest.guestSurvey;
		}

		return result;
	}

	public trackingGuestStatus(guestId: number): string | any{
		let completedGuests: any;
		let inProgressGuests: any;
		this.store.pipe(select(fromRoot.getGuestListCompleted)).subscribe(guestsCompleted => {
			completedGuests = guestsCompleted.filter((completed: any) => guestId === completed.guestId);
		});
		if (completedGuests.length) {
			return analyticsConstants.GUEST_STATUS.COMPLETED;
		}
		this.store.pipe(select(fromRoot.getGuestListNotCompleted)).subscribe(guestsNoCompleted => {
			inProgressGuests = guestsNoCompleted.filter((guestInProggress: any) => guestId === guestInProggress.guestId);
		});
		if (inProgressGuests.length) {
			return analyticsConstants.GUEST_STATUS.IN_PROGRESS;
		}
	}

	isTravelQuestionnaireCompleteForGuest(guest: Guest): boolean {
		let travelQuestionnaires: ITravelQuestionnaire[] = [];
		this.store
			.pipe(
				select(fromRoot.getTravelQuestionnaire),
				take(1)
			)
			.subscribe(tq => {
				travelQuestionnaires = tq && tq.length ? tq : [];
			});
		if (travelQuestionnaires && !travelQuestionnaires.length) {
			return true;
		}
		const tqForGuest = travelQuestionnaires.find(tq => tq.guestId === guest.guestId);
		return !!(tqForGuest && !!tqForGuest.hq1); //&& !!tqForGuest.hq2;
	}

	public isTravelQuestionnaireSectionCompleted(): Observable<boolean> {
		const guestsSelectedForCheckin = this.selectedForCheckin ? this.selectedForCheckin : [];
		if (guestsSelectedForCheckin && !guestsSelectedForCheckin.length) {
			return of(true);
		}
		let travelQuestionnaire: ITravelQuestionnaire[] = [];
		this.store
			.pipe(
				select(fromRoot.getTravelQuestionnaire),
				take(1)
			)
			.subscribe(tq => {
				travelQuestionnaire = tq && tq.length ? tq : [];
			});

		if (travelQuestionnaire && !travelQuestionnaire.length) {
			return of(true);
		}

    const guestSelectedNoResponses = [];
    guestsSelectedForCheckin.forEach((guest: any) => {
      const existTq = travelQuestionnaire.find(q => q.guestId === guest.guestId);
      if (!existTq || !existTq.hq1) { //|| !existTq.hq2
        guestSelectedNoResponses.push(existTq);
      }
    });
    return of(!guestSelectedNoResponses.length);
	}

	get isTravelQuestionnaireCompleted(): Observable<boolean> {
		return this.isTravelQuestionnaireSectionCompleted();
	}

	isGuestDetailsCompleted(): Observable<boolean> {
    this.firstPageModules = this.firstPageModules.filter(modules => modules !== constants.MODULES_NAMES.CONTACT_INFO);
    return this.isFeatureCompleted(this.firstPageModules)
	}

	isGuestDetailComplete(g: any) {
		return !(!g.moduleSpecificValue.personalDetails ||
      !g.moduleSpecificValue.addressContact ||
      !g.moduleSpecificValue.immigrationInfo ||
      !g.moduleSpecificValue.emergencyContact ||
      !this.isTravelQuestionnaireCompleteForGuest(g));
	}

	get isOnboardExpensesCompleted(): Observable<boolean> {
		const oeModules = [...this.firstPageModules, constants.MODULES_NAMES.EXPENSE_ACCOUNT];
		return this.isFeatureCompleted(oeModules);
	}

	get isCruiseDetailsCompleted(): Observable<boolean> {
		const cruiseCModules = [...this.firstPageModules, constants.MODULES_NAMES.EXPENSE_ACCOUNT, constants.MODULES_NAMES.CRUISE_CONTRACT, constants.MODULES_NAMES.HEALTH_RISK_ACKNOWLEDGEMENT];
		return this.isFeatureCompleted(cruiseCModules);
	}

	get isArrivalInfoCompleted(): Observable<boolean> {
		const cruiseCModules = [...this.firstPageModules, constants.MODULES_NAMES.EXPENSE_ACCOUNT, constants.MODULES_NAMES.CRUISE_CONTRACT];
		if (this.isSmartShip && this.isArrivalTimesEnabled) {
			cruiseCModules.push(constants.MODULES_NAMES.ARRIVAL_TIMES);
		}
		return this.isFeatureCompleted(cruiseCModules);
	}

	getLoggedInGuestGA(): any {
		let loggedInGuest;
		this.store
			.pipe(
				select(fromRoot.getLoggedInGuestGA),
				take(1)
			)
			.subscribe(vds => (loggedInGuest = vds));
		return loggedInGuest;
	}

	getGuest(guestId: number | null): Guest | any {
		return this._guests.find((guest: any) => guest.guestId === guestId);
	}

	get selectedForCheckin() {
		return this._guests.filter((guest: any) => guest.selectedForCheckin);
	}

	getGuestOlciStatus(guestId: number) {
		return this._guests.find((r: any) => r.guestId === guestId)?.olciStatus;
	}

	getGuestsCompleted() {
		return this._guestsCompleted;
	}

	getAllGuestsReservationIds(): IRetrieveCheckinStatusRequestPayload {
		const guests = this._guests;
		return {
			guestReservationIds: this.getAllGuestsBookingCombination(guests)
		};
	}

	public isGuestAMinor(dateOfBirth: string): boolean {
		const dob = moment(dateOfBirth, constants.DATE_FORMAT.SERVER);
		const departureDate = moment(this.sessionSnapshot.departureDate, constants.DATE_FORMAT.SERVER);
		const guestAge = departureDate.diff(dob, 'years');
		return guestAge < 18;
	}

	getFirstAdultInTheReservation(): Guest {
		const guest = this._guests.find((g: any) => g.minor === false);
		return guest ? guest : {} as Guest;
	}

	getGroupedByBookingId(guests: Guest[]) {
		const groupedGuests: { [index: string]: Guest[] } = {};

		let source: Guest[] = guests;

		if (guests === null) {
			source = this._guests;
		}

		source.forEach(guest => {
			if (!groupedGuests[guest.bookingId]) {
				groupedGuests[guest.bookingId] = [];
			}

			groupedGuests[guest.bookingId].push(guest);
		});

		return groupedGuests;
	}

	isGuestLocked(guest: Guest) {
		return guest && guest.isLocked;
	}

	isAnyGuestLocked() {
		return this._guests.some((g: any) => this.isGuestLocked(g));
	}

	public get loggedInGuestPaxId() {
		const session = this.bookingService.snapshot;
		if (session && session.loggedInPaxId) {
			const isPartOfTheBooking = this._guests.find((g: any) => Number(g.guestId) === Number(session.loggedInPaxId));
			return isPartOfTheBooking ? isPartOfTheBooking.guestId : null;
		}
		return null;
	}

	getFirstDosmeticGuest(): Guest | any {
		if(!this._guests.length){
			return null;
		}
		return this._guests.find((guest: any) => guest.bookingOfficeCode === constants.BOOKING_OFFICE.MIA);
	}

	public saveIsYourFirstCruiseVacation(surveyResponse: boolean) {
		const loggedInGuestInGuestAccount = this.getGuest(this.loggedInGuestPaxId);
		const payload = {
			guestId: this.loggedInGuestPaxId?.toString(),
			guestSurvey: {
        bookingId: loggedInGuestInGuestAccount.bookingId,
				cruiseCountWithUs: '0',
				cruiseCount: '0',
				firstCruiseVacation: constants.CRUISE_SURVEY.YES
			},
			middlewarePayload: {
				passengerSurvey: {
					bookingId: loggedInGuestInGuestAccount.bookingId,
					guestId: this.loggedInGuestPaxId,
					firstTimeCruise: '1'
				}
			}
		};

		if (!surveyResponse) {
			payload.guestSurvey.firstCruiseVacation = constants.CRUISE_SURVEY.NO;
			payload.middlewarePayload.passengerSurvey.firstTimeCruise = '0';
		}
		this.store.dispatch(new guestActions.PostSurveyAction(payload));
		this.store.dispatch(new guestDetailsActions.SaveGuestSurveyAction(payload));
	}

  public getGuestsProfileBooking(
    payload: IProfileBookingPayload
  ): Observable<any> {
    this.updateGuestProfileBookingData(payload);
    return this.microService.getProfileBookings(payload).pipe(
      map((profileBookings) => {
        if (profileBookings.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
          this.store.dispatch(new olci.NavigateToError());
          return null;
        } else {
          return this.mapProfileBookingResponse(profileBookings);
        }
      }),
      catchError((error) => {
        return of(
          new guestActions.LoadGuestsProfileBookingMicroserviceAction(error)
        );
      })
    );
  }

  private updateGuestProfileBookingData(guestData: IProfileBookingPayload): void {
    this.guestProfileBookingData = guestData;
    this.guestProfileBookingData.sailDate = formatDateWithoutDashes(this.sessionSnapshot.departureDate);
    this.guestProfileBookingData.shipCode = this.sessionSnapshot.shipCode;
  }

  public mapProfileBookingResponse({ status, errors, payload }: GenericResponse<ProfileBookingPayload>) {
    const session = this.sessionSnapshot;
    const { profileBookings: bookings = []} = payload;
    const [ booking = null ] = bookings;
    const guests: Guest[] = [];
    const loggedInGuestInfo = booking ? {
      bookingId: booking?.bookingId,
      guestId: booking?.grantorPassengerId
    } : null;

    this.saveLoggedInGuestIdInStore(loggedInGuestInfo);

    if (bookings.length) {
      bookings.forEach(
        ({ passengers: bookingGuests, bookingId, officeCode, bookingCurrency }: ProfileBooking) => {
          bookingGuests.forEach((currentGuest: Passenger) => {
            const dob = moment(currentGuest.birthdate, constants.DATE_FORMAT.MICROSERVICE);
            const departureDate = moment(session.departureDate, constants.DATE_FORMAT.SERVER);
            const guestAge = departureDate.diff(dob, 'years');
            const isMinor = guestAge < 18;

            const guest: Guest = {
              firstName: utils.capitalizeNameAllWords(currentGuest.firstName),
              middleName: '',
              lastName: utils.capitalizeNameAllWords(currentGuest.lastName),
              suffix: '',
              consumerId: currentGuest.consumerId,
              guestId: parseInt(currentGuest.passengerId),
              olciStatus: currentGuest.onlineCheckinStatus,
              bookingId: parseInt(bookingId),
              bookingOfficeCode: officeCode ? officeCode : constants.BOOKING_OFFICE.MIA,
              moduleSpecificValue: this.modulesValuesByOlciStatus(currentGuest.onlineCheckinStatus),
              minor: isMinor,
              selectedForCheckin: true,
              countryCode: '',
              selectedLanguageCode: '',
              base64Image: '',
              photoId: '',
              photoUri: '',
              boardingPassPendingStatus: false,
              isLocked: false,
              bookingCurrency
            };
            guests.push(guest);
          });
        });
    }

    return {
      status,
      errors,
      payload: guests
    };
  }

	public saveLoggedInGuestIdInStore(loggedInGuest: any) {
		if (!loggedInGuest) {
			return;
		}
		this.store.dispatch(new olci.UpdateLoggedInGuestInfoAction(loggedInGuest));
	}

	public getAllGuestsBookingCombination(guests?: Guest[] | null) {
		let guestBookingArray: any = [];
		const guestList = guests && guests?.length ? guests : this._guests;
		if (guestList?.length) {
			guestBookingArray = guestList.map((g: any) => {
				return { guestId: g.guestId, bookingId: g.bookingId };
			});
		}
		return guestBookingArray;
	}

	public createSecurityPhotoPayload(guest: Guest[]) {
		const guestsBooking = this.getAllGuestsBookingCombination(guest);
		return {
			reservationIds: guestsBooking
		};
	}

	public modulesValuesByOlciStatus(status: string): ModuleSpecificValue {
		const session = this.sessionSnapshot;

		if (status === constants.OLCI_STATUS.COMPLETED || status === constants.OLCI_STATUS.REPRINT) {
			return {
				addressContact: true,
				arrivalTimes: session.enhancedSSP,
				contactInfo: true,
				cruiseContract: true,
				emergencyContact: true,
				expenseAccount: true,
				expressPassSmartShips: true,
				healthRiskAcknowledgement: true,
				immigrationInfo: true,
				personalDetails: true
			};
		} else {
			return {
				addressContact: false,
				arrivalTimes: false,
				contactInfo: false,
				cruiseContract: false,
				emergencyContact: false,
				expenseAccount: false,
				expressPassSmartShips: false,
				healthRiskAcknowledgement: false,
				immigrationInfo: false,
				personalDetails: true
			};
		}
	}

	public modulesValuesFromPercentages(payload: IRetrieveCheckinStatusResponse) {
		const response = payload;

		if (!response.payload) {
			return response;
		} else {
			response.payload.checkinStatuses = response.payload.checkinStatuses.map(checkinStatus => {
				return Object.assign({}, checkinStatus, {
					moduleSpecificValue: this.mapPercentages(checkinStatus)
				});
			});
		}
		return response;
	}

	public mapPercentages(checkinStatus: any): ModuleSpecificValue {
		return {
			addressContact: checkinStatus.isAddressComplete,
			arrivalTimes: checkinStatus.isAppointmentComplete,
			contactInfo: checkinStatus.isContactInfoComplete,
			cruiseContract: checkinStatus.isReservationContractComplete,
			emergencyContact: checkinStatus.isEmergencyContactComplete,
			expenseAccount: checkinStatus.isExpenseComplete,
			expressPassSmartShips: checkinStatus.isBoardingPassComplete,
			healthRiskAcknowledgement: checkinStatus.isHealthRiskAcknowledgementComplete,
			immigrationInfo: checkinStatus.isTravelDocumentsComplete,
			personalDetails: checkinStatus.isPersonalDetailComplete
		};
	}

	public resetSelectedGuestState() {
		this.store.dispatch(new actions.SetCurrentGuestIdAction(null));
		this.store.dispatch(new actions.ClearSelectedGuestAction());
		this.store.dispatch(new guestActions.ResetSelectedOtherGuest());
	}
}
